import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ticketsApi } from '@/api/tickets.api';
import { Modal } from '@/components/ui/Modal';
import { Field } from '@/components/ui/Field';
import { CommunicationMethod } from '@/types/domain';
import { ApiError } from '@/lib/errors';
import { useToasts } from '@/context/ToastContext';
import { invalidateAfterTicketAction } from '@/lib/queryInvalidation';
import { useTagTeammates } from './useTagTeammates';

interface Props {
  ticketId: string;
  open: boolean;
  onClose: () => void;
}

export function LogCxModal({ ticketId, open, onClose }: Props) {
  const [method, setMethod] = useState<CommunicationMethod>(CommunicationMethod.Email);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { push } = useToasts();
  const tag = useTagTeammates();

  const mutation = useMutation({
    mutationFn: async () => {
      const res = await ticketsApi.logCx(ticketId, { method, note: note || undefined });
      if (tag.hasContent) await ticketsApi.tag(ticketId, tag.payload());
      return res;
    },
    onSuccess: () => {
      // V4.9 — after logging CX, refresh the ticket detail AND every list
      // that shows the CX badge. Without invalidating ['tickets'] and
      // ['myQueue'], the "Never informed" pill stayed red on those tables
      // until the user manually reloaded — confusing.
      push({ kind: 'success', title: 'CX communication logged', message: 'Customer is now marked as Informed.' });
      invalidateAfterTicketAction(queryClient, ticketId);
      onClose();
      setNote('');
      tag.reset();
    },
    onError: (err: unknown) => {
      setError(err instanceof ApiError ? err.problem?.detail ?? err.message : 'Failed to log CX');
    }
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Log CX communication"
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending ? 'Logging...' : 'Log communication'}
          </button>
        </>
      }
    >
      <Field label="Method" required>
        <select
          className="form-select"
          value={method}
          onChange={(e) => setMethod(e.target.value as CommunicationMethod)}
        >
          {Object.values(CommunicationMethod)
            .filter((m) => m !== CommunicationMethod.Close)
            .map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
        </select>
      </Field>
      <Field label="Note (optional)">
        <textarea
          className="form-textarea"
          rows={3}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </Field>
      {tag.render()}
      {error ? <div className="form-error">{error}</div> : null}
    </Modal>
  );
}
